import { useEffect, useState, useMemo } from 'react'
import { Button, Input } from '../ui';
import { ArrowLeft, Mail, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchSurveyResults } from '../../api/survey.api';
import { LoadingState } from '../common';

const PAGE_SIZE = 6;

const statusConfig = {
  responded: { label: "Répondu", className: "text-success" },
  invited: { label: "En attente", className: "text-accent" },
  bounced: { label: "Erreur", className: "text-destructive" },
};

const Participants = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  console.log({surveyData});
  

  useEffect(() => {
    const loadParticipants = async () => {
      try {
        setLoading(true);
        const response = await fetchSurveyResults(id);
        setSurveyData(response?.data || response);
      } catch (err) {
        console.error('Erreur lors du chargement des participants:', err);
        setError('Erreur lors du chargement des participants');
        toast.error('Erreur lors du chargement des participants');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadParticipants();
    }
  }, [id]);

  const survey = surveyData?.survey || {};
  const stats = surveyData?.stats || {};
  const participants = surveyData?.participants || [];

  const filtered = useMemo(
    () => participants.filter((p) => 
      (p.prenom?.toLowerCase() || '').includes(search.toLowerCase()) || 
      (p.nom?.toLowerCase() || '').includes(search.toLowerCase()) || 
      (p.email?.toLowerCase() || '').includes(search.toLowerCase())
    ),
    [participants, search]
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const responded = participants.filter((p) => p.status === "responded").length;
  const rate = participants.length > 0 ? Math.round((responded / participants.length) * 100) : 0;

  if (loading) {
    return <LoadingState message="Chargement des participants..." />;
  }

  if (error || !surveyData) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />Retour
        </Button>
        <div className="text-center py-12">
          <p className="text-red-600">{error || 'Aucune donnée disponible'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />Retour au sondage
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Participants</h1>
          <p className="text-sm text-muted-foreground mt-1">Suivi des invitations — {survey.title}</p>
        </div>
        {/* <Button variant="outline" className="gap-2" onClick={() => toast.success("Rappels envoyés aux participants en attente")}>
          <Mail className="h-4 w-4" />Envoyer rappels
        </Button> */}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-foreground">{participants.length}</p>
            <p className="text-xs text-muted-foreground">Total invités</p>
          </CardContent>
        </Card>
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-success">{responded}</p>
            <p className="text-xs text-muted-foreground">Réponses reçues</p>
          </CardContent>
        </Card>
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-primary">{rate}%</p>
            <p className="text-xs text-muted-foreground">Taux de complétion</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un participant..."
          className="pl-9"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* List */}
      <Card className="shadow-card border-0">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {paginated.map((p) => {
              const status = statusConfig[p.status] || statusConfig.invited;
              const fullName = `${p.prenom || ''} ${p.nom || ''}`.trim() || 'Participant anonyme';
              return (
                <div key={p.id} className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                      {fullName.split(" ").map((n) => n[0]).join("").toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{fullName}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.updated_at && <span className="text-xs text-muted-foreground">{new Date(p.updated_at).toLocaleDateString('fr-FR')}</span>}
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${status.className}`}>
                      {status.label}
                    </div>
                  </div>
                </div>
              );
            })}
            {paginated.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">Aucun participant trouvé.</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {filtered.length} participant{filtered.length > 1 ? "s" : ""} — Page {page}/{totalPages}
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Participants;
